import {
  NetworkStatus,
  useLazyQuery,
  usePaginationHelper,
  useQuery,
  FiltersType,
  useMutation,
} from '@common/hooks';
import {
  OrderDirection,
  WorkerOrderField,
} from '@common/graphql/schema/codegen-graphql';
import React, { useEffect, useState } from 'react';
import { CandidateLink } from 'works/legacy/components/candidate-link';
import {
  buildOptionsFromData,
  ErrorHandler,
  FilteredTable,
  filtersForPaginatedAttr,
  Gutter,
  Modal,
  MultiSelectTableFilter,
  Text,
  TextOverflow,
  TextSearchTableFilter,
  TimeWithTimeZone,
} from '@common/components';
import { RolesQuery } from '@clinician-profile/graphql';
import { createMessage } from 'works/legacy/lib/message-helper';
import { AdminWorkforceGroupStaffTableBatchActions } from './_admin-workforce-group-staff-table-batch-action-buttons';
import { MoveConfirmationModal } from './_move-confirmation-modal';

import {
  BULK_DEACTIVATE_WORKERS_MUTATION,
  BULK_UPDATE_WORKERS_WORKFORCE_GROUP_MUTATION,
} from '@scheduling/graphql/mutations/bulk-workers';
import { PAGINATED_WORKERS_QUERY } from '@scheduling/graphql';
import { ORGANIZATION_UNITS_FLAT_QUERY } from '@institutions/graphql';

export function WorkforceGroupStaffTable({
  workforceGroupId,
  onFiltersChange,
}: {
  workforceGroupId: string;
  onFiltersChange?: (filters: FiltersType) => void;
}) {
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [moveModalVisible, setMoveModalVisible] = useState(false);

  const {
    filters,
    paginationVariables,
    localStorageFilterKey,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey: 'AdminGroupStaffTable',
    defaultPaginationData: {
      defaultPageSize: 100,
      limit: 100,
      offset: 0,
      orderDirection: 'ASC',
      orderField: 'NAME',
    },
    columnsDictionary: {
      name: 'NAME',
      homeUnit: 'HOME_UNIT',
      createdAt: 'CREATED_AT',
    },
  });

  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange(filters);
    }
  }, [filters, onFiltersChange]);

  const {
    loading: rolesLoading,
    error: rolesError,
    data: rolesData,
  } = useQuery(RolesQuery);

  const [workersQuery, { error, data, loading, networkStatus }] = useLazyQuery(
    PAGINATED_WORKERS_QUERY,
    {
      notifyOnNetworkStatusChange: true,
    }
  );

  const [bulkDeactivateWorkers] = useMutation(
    BULK_DEACTIVATE_WORKERS_MUTATION,
    {
      refetchQueries: ['PaginatedWorkersQuery'],
      onCompleted: () => {
        createMessage('success', 'Staff members deactivated successfully.');
        setSelectedRowKeys([]);
      },
      onError: () => {
        createMessage('error', 'Failed to deactivate staff members.');
      },
    }
  );

  const [bulkUpdateWorkersWorkforceGroup, { loading: moveLoading }] =
    useMutation(BULK_UPDATE_WORKERS_WORKFORCE_GROUP_MUTATION, {
      refetchQueries: ['PaginatedWorkersQuery'],
      onCompleted: () => {
        createMessage('success', 'Staff members moved successfully.');
        setSelectedRowKeys([]);
        setMoveModalVisible(false);
      },
      onError: () => {
        createMessage('error', 'Failed to move staff members.');
      },
    });

  useEffect(() => {
    workersQuery({
      variables: {
        limit: paginationVariables.limit,
        offset: paginationVariables.offset,
        orderDirection: paginationVariables.orderDirection as OrderDirection,
        orderField: paginationVariables.orderField as WorkerOrderField,
        filters: {
          ...filters,
          workforceGroup: [workforceGroupId],
          active: ['true'],
        },
      },
    });
  }, [filters, paginationVariables, workersQuery, workforceGroupId]);

  const {
    loading: organizationUnitLoading,
    data: organizationUnitData,
    error: organizationUnitError,
  } = useQuery(ORGANIZATION_UNITS_FLAT_QUERY);

  const anyError = error || rolesError || organizationUnitError;

  if (anyError) {
    return <ErrorHandler error={anyError} log="WorkforceGroupStaffTable" />;
  }

  const isLoaded =
    networkStatus === NetworkStatus.ready &&
    workforceGroupId &&
    data &&
    organizationUnitData &&
    !organizationUnitLoading &&
    !rolesLoading;
  const workers = isLoaded ? data.paginatedWorkers.workers : [];
  const total = isLoaded ? data.paginatedWorkers.totalCount : 0;

  const handleDeactivate = () => {
    Modal.confirm({
      title: `Deactivate ${selectedRowKeys.length} staff?`,
      content:
        'Staff will no longer appear in this group. You can reactivate them anytime from Team tab.',
      okText: 'Yes, Deactivate',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk: () => {
        bulkDeactivateWorkers({
          variables: {
            workerIds: selectedRowKeys,
          },
        });
      },
    });
  };

  const handleMove = (workforceGroupId: string) => {
    bulkUpdateWorkersWorkforceGroup({
      variables: {
        workerIds: selectedRowKeys,
        workforceGroupId,
      },
    });
  };

  const handleCancelSelection = () => {
    setSelectedRowKeys([]);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: string[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    columnWidth: 40,
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'worksUser',
      key: 'name',
      width: '20%',
      sorter: true,
      render: (
        worksUser: { firstName: string; lastName: string },
        record: { id: string }
      ) => {
        return (
          <CandidateLink
            firstName={worksUser.firstName}
            lastName={worksUser.lastName}
            link={`/works/team/on_demand/${record.id}`}
          />
        );
      },
    },
    {
      title: 'EEID',
      dataIndex: 'employeeId',
      key: 'employeeId',
      width: '10%',
      filtered: filters.employeeId || false,
      filteredValue: filters.employeeId || null,
      filterDropdown: TextSearchTableFilter,
    },
    {
      title: 'Role',
      key: 'role',
      width: '7%',
      filtered: filters.role || false,
      filteredValue: filters.role || null,
      filters: filtersForPaginatedAttr(
        rolesData && rolesData.roles,
        (role: { abbreviation: string }) => role.abbreviation,
        (role: { abbreviation: string; id: string }) => ({
          text: role.abbreviation,
          value: role.id,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      render: (record: { role: { abbreviation: string } }) => {
        return <>{record.role.abbreviation}</>;
      },
    },
    {
      title: 'Home Unit',
      dataIndex: 'homeUnit',
      key: 'homeUnit',
      width: '20%',
      filtered: filters.homeUnit || false,
      filteredValue: filters.homeUnit || null,
      filters: buildOptionsFromData(
        organizationUnitData?.organizationUnits || [],
        (organizationUnit: { name: string; id: string }) => ({
          text: organizationUnit.name,
          value: organizationUnit.id,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      sorter: true,
      render: (record: { name: string; costCenter: string } | null) => {
        if (!record) {
          return <>None</>;
        }
        return (
          <>
            <Text>{record.name}</Text>
            <br />
            <Text secondary>{record.costCenter}</Text>
          </>
        );
      },
    },
    {
      title: 'Approved Units',
      dataIndex: 'allowList',
      key: 'allowList',
      width: '33%',
      filtered: filters.allowList || false,
      filteredValue: filters.allowList || null,
      filters: filtersForPaginatedAttr(
        organizationUnitData?.organizationUnits,
        ({ name }: { name: string }) => name,
        ({ id, name }: { id: string; name: string }) => ({
          text: name,
          value: id,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      render: (
        _: unknown,
        record: {
          allowedUnits: Array<{ name: string }>;
          homeUnit?: { name: string };
        }
      ) => {
        const allowedUnitsText = record?.allowedUnits.reduce(
          (prev: string, unit: { name: string }) => {
            const isHomeUnit = record?.homeUnit?.name === unit.name;
            return `${prev}, ${unit.name}${isHomeUnit ? ' (Home Unit)' : ''}`;
          },
          ''
        );

        return <TextOverflow>{allowedUnitsText.substring(2)}</TextOverflow>;
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: '10%',
      sorter: true,
      render: (createdAt: moment.Moment) => (
        <TimeWithTimeZone date={createdAt} variant="long_format" />
      ),
    },
  ];

  return (
    <>
      {selectedRowKeys.length > 0 && (
        <Gutter size="md" direction="bottom">
          <AdminWorkforceGroupStaffTableBatchActions
            selectedCount={selectedRowKeys.length}
            totalCount={total ?? 0}
            onDeactivate={handleDeactivate}
            onMove={() => setMoveModalVisible(true)}
            onCancel={handleCancelSelection}
          />
        </Gutter>
      )}

      <FilteredTable
        columns={columns}
        tableLayout="fixed"
        rowKey="id"
        dataSource={workers}
        loading={loading}
        pagination={{ ...paginationVariables, total }}
        onChange={tableChangeHandler}
        onFilterChange={filterChangeHandler}
        localStorageFilterKey={localStorageFilterKey}
        filters={filters}
        rowSelection={rowSelection}
      />

      <MoveConfirmationModal
        visible={moveModalVisible}
        selectedCount={selectedRowKeys.length}
        currentGroupId={workforceGroupId}
        loading={moveLoading}
        onConfirm={handleMove}
        onCancel={() => setMoveModalVisible(false)}
      />
    </>
  );
}
